import type { SlotRenderHooks } from '@zag/calendar/models';

/**
 * Resolver returning a localised label for an absence-type key.
 * Receives the raw `data.absenceType` value (with or without `absence:` prefix)
 * and returns the localised label or `undefined` when the key is unknown.
 */
export type AbsenceTypeLabelResolver = (typeKey: string) => string | undefined;

const ABSENCE_TYPE_FIELD = 'absenceType';
const ABSENCE_TYPE_PREFIX = 'absence:';
const ABSENCE_DATA_TYPE = 'absence';
const ABSENCE_DEFAULT_ICON = 'event_busy';
const ABSENCE_DEFAULT_COLOR = '#f97316';

export function isAbsenceSlotData(raw: Record<string, unknown>): boolean {
  const type = raw['type'];
  return typeof type === 'string' && (type === ABSENCE_DATA_TYPE || type.startsWith(ABSENCE_TYPE_PREFIX));
}

export function deriveAbsenceTypeKey(raw: Record<string, unknown>): string | undefined {
  const rawType = raw[ABSENCE_TYPE_FIELD];
  if (typeof rawType === 'string' && rawType.length > 0) {
    return rawType.startsWith(ABSENCE_TYPE_PREFIX) ? rawType : `${ABSENCE_TYPE_PREFIX}${rawType}`;
  }

  const slotType = raw['type'];
  if (typeof slotType === 'string' && slotType.startsWith(ABSENCE_TYPE_PREFIX)) {
    return slotType;
  }

  return undefined;
}

/**
 * Derive a display title for an absence slot from its `absenceType` key.
 * Tries the resolver first (both prefixed and stripped variants), then falls
 * back to a capitalised key, finally to `undefined` so the caller can apply
 * its own ultimate default.
 */
export function deriveAbsenceSlotTitle(raw: Record<string, unknown>, labelResolver?: AbsenceTypeLabelResolver): string | undefined {
  const prefixed = deriveAbsenceTypeKey(raw);
  if (!prefixed) return undefined;
  const stripped = prefixed.slice(ABSENCE_TYPE_PREFIX.length);

  const fromResolver = labelResolver?.(prefixed) ?? labelResolver?.(stripped);
  if (fromResolver && fromResolver.length > 0) return fromResolver;

  if (stripped.length === 0) return undefined;
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
}

/**
 * Build a {@link SlotRenderHooks} bundle that knows how to render absence
 * slots (slots whose `data.type === 'absence'`). The calendar render layer
 * itself stays oblivious to absences — this factory keeps that knowledge
 * inside the timetracking module.
 *
 * @param labelResolver Optional translator for absence-type keys. Without a
 * resolver the title falls back to a capitalised type key (e.g. `vacation` →
 * `Vacation`).
 */
export function createAbsenceSlotRenderHooks(labelResolver?: AbsenceTypeLabelResolver): SlotRenderHooks {
  return {
    resolveTitle: (raw: Record<string, unknown>) => (isAbsenceSlotData(raw) ? deriveAbsenceSlotTitle(raw, labelResolver) : undefined),
    resolveIcon: (raw: Record<string, unknown>) => (isAbsenceSlotData(raw) ? ABSENCE_DEFAULT_ICON : undefined),
    resolveColor: (raw: Record<string, unknown>) => (isAbsenceSlotData(raw) ? ABSENCE_DEFAULT_COLOR : undefined),
    resolveIsAllDay: (raw: Record<string, unknown>) => (isAbsenceSlotData(raw) ? true : undefined),
    isEditableOverride: (raw: Record<string, unknown>) => isAbsenceSlotData(raw),
    resolveHiddenFormFields: (slotTypeKey: string, category?: string) =>
      slotTypeKey === ABSENCE_DATA_TYPE || category === ABSENCE_DATA_TYPE ? ABSENCE_HIDDEN_FORM_FIELDS : undefined,
  };
}

const ABSENCE_HIDDEN_FORM_FIELDS: ReadonlyArray<string> = ['title', 'description', 'locationOverride'];
